import {
  Clock20Px,
  ModeGoodsIn20Px,
  ModeGoodsOut20Px,
  Error20Px,
  Service20Px,
  Hold20Px,
  Diagnose20Px,
  andonColors,
  mainColours
} from "@qubit/autoparts";
import type { TFunction } from "i18next";
import { Layout } from "react-grid-layout";

import { getEmptyPositions } from "~/lib/helpers";
import {
  AndonWorkstation,
  FulfillmentCenterDto,
  SystemMode,
  WorkstationSummaryDto
} from "~/types/api";

import { hasEventType, isAutostoreEvent, isHandToggled } from "./andon.logic";

import { BORDER_OVERLAY_RGL_MARGIN } from "./andon.styles";
import {
  AndonStatus,
  AndonColorState,
  AltTextKey,
  AndonEventWithMeta,
  AndonDisplayLine,
  ANDON_DISPLAY_LINE_LABEL_KEY,
  AndonCellBorder,
  AndonWorkstationTooltipContent,
  AndonBoardLayoutItem,
  AndonBoardTextLayoutItem,
  AndonBoardWorkstationLayoutItem,
  AndonBoardWorkstationSelection,
  BoardDimensions,
  BoardLayout,
  WorkingLayout,
  DEFAULT_ANDON_DISPLAY_LINES,
  TEXT_ELEMENT_PREFIX,
  WorkstationWithFulfillmentCenter,
  BorderSide
} from "./andon.types";
const newAndonColors = {
  handRaisedYellow: "#ffc043",
  handRaisedBlockerRed: andonColors.blockerRed,
  portFaultAmber: "#E11B22",
  gridDownPurple: andonColors.gridDownPurple,
  openGreen: "#15844B",
  workstationActiveGrey: "grey.600"
};

const andonColorStates: Record<string, AndonColorState> = {
  handRaisedYellow: {
    backgroundColor: newAndonColors.handRaisedYellow,
    borderColor: "#BA8A2C",
    textColor: "black",
    icon: Hold20Px,
    altTextKey: "hand raised"
  },
  handRaisedBlockerRed: {
    backgroundColor: newAndonColors.handRaisedBlockerRed,
    borderColor: "#9e0916",
    textColor: "white",
    icon: Hold20Px,
    altTextKey: "hand raised"
  },
  portFaultAmber: {
    backgroundColor: newAndonColors.portFaultAmber,
    borderColor: "#E11B22",
    textColor: "white",
    icon: Error20Px,
    altTextKey: "port fault"
  },
  gridDownPurple: {
    backgroundColor: newAndonColors.gridDownPurple,
    borderColor: "#6969ff",
    textColor: "black",
    icon: Error20Px,
    altTextKey: "grid error"
  },
  openGreen: {
    backgroundColor: newAndonColors.openGreen,
    borderColor: "#189A57",
    textColor: "white",
    icon: null
  },
  workstationActiveGrey: {
    backgroundColor: newAndonColors.workstationActiveGrey,
    borderColor: "#D6D3D3",
    textColor: "white",
    icon: null
  },
  default: {
    backgroundColor: "gray.light",
    borderColor: "#d6d3d3",
    textColor: "black",
    icon: null,
    altTextKey: "inactive"
  }
};

// Tile colors and icon from workstation, port, hand-raise, and grid system state.
export const figureAndonColorFromStatus = ({
  handRaised,
  handRaiseRequestType,
  portStatus,
  workstationStatus,
  gridSystemMode,
  workstationActive,
  workstationMode
}: {
  handRaised?: boolean;
  handRaiseRequestType?: string | null;
  gridSystemMode?: SystemMode | null;
  workstationStatus?: AndonStatus;
  portStatus?: AndonStatus;
  workstationActive?: boolean;
  workstationMode?: WorkstationSummaryDto["mode"];
}): {
  backgroundColor: string;
  borderColor: string;
  textColor: string;
  icon: React.ElementType | null;
  altTextKey?: AltTextKey;
} => {
  if (handRaised) {
    return handRaiseRequestType === "blocker"
      ? andonColorStates.handRaisedBlockerRed
      : andonColorStates.handRaisedYellow;
  }
  // ALT = ALERT ; DSTP = STOPPING
  if (gridSystemMode && ["ALT", "DSTP", "SVC", "STP"].includes(gridSystemMode))
    return andonColorStates.gridDownPurple;
  if (workstationStatus === "bad" || portStatus === "bad")
    return andonColorStates.portFaultAmber;
  if (workstationActive && workstationMode === "Standby")
    return andonColorStates.workstationActiveGrey;

  if (workstationActive && workstationMode && workstationMode !== "Standby")
    return andonColorStates.openGreen;

  return andonColorStates.default;
};

// Event explorer badge color and abbreviation letters for an event.
export const mapEventColorAndLetters = (event: AndonEventWithMeta) => {
  let colorToReturn = "gray.light";
  let lettersToReturn = "";

  const { event: payload, hub } = event;

  // LEGACY eventType events
  if (hasEventType(payload)) {
    switch (payload.eventType) {
      case "Selected":
        colorToReturn = mainColours.label["1"];
        lettersToReturn = "PS";
        break;
      case "LabelPlaced":
        colorToReturn = mainColours.label["9"];
        lettersToReturn = "LP";
        break;
      case "StateChange":
        colorToReturn = mainColours.label["5"];
        lettersToReturn = "PTL";
        break;
      case "Position Confirmed":
        colorToReturn = mainColours.label["6"];
        lettersToReturn = "PLA";
        break;
      case "Completed":
        colorToReturn = mainColours.label["10"];
        lettersToReturn = "C";
        break;
    }
  }

  // AUTOSTORE events
  if (isAutostoreEvent(payload)) {
    switch (payload.case) {
      case "PortMode":
        switch (payload.event.portMode) {
          case "Open":
            colorToReturn = "white";
            lettersToReturn = "PO";
            break;
          case "Closed":
            colorToReturn = "#404040";
            lettersToReturn = "PC";
            break;
          case "MultiPortPuppet":
            colorToReturn = "#00000017";
            lettersToReturn = "PM";
            break;
        }
        break;
      case "PortError":
        colorToReturn = payload.event.hasError
          ? andonColors.safetyStopRed
          : andonColors.openGreen;
        lettersToReturn = payload.event.hasError ? "PE" : "POK";
        break;
      case "SystemModeChanged":
        colorToReturn = ["ALT", "DSTP", "STP", "SVC"].includes(
          payload.event.systemMode
        )
          ? andonColors.safetyStopRed
          : andonColors.openGreen;
        lettersToReturn = ["ALT", "DSTP", "STP", "SVC"].includes(
          payload.event.systemMode
        )
          ? "SMNOK"
          : "SMOK";
        break;
      case "HandToggled":
        colorToReturn = payload.event.raiseHand
          ? andonColors.handRaisedYellow
          : "#90EE90"; // Light green for hand down
        lettersToReturn = payload.event.raiseHand ? "HU" : "HD";
        break;
      case "BinRequested":
        colorToReturn = mainColours.label["2"];
        lettersToReturn = "PLR";
        break;
      case "PTLMessageRegistered":
        colorToReturn = mainColours.label["3"];
        lettersToReturn = "PLR";
        break;
      case "PTLMessageActivated":
        colorToReturn = mainColours.label["6"];
        lettersToReturn = "PLA";
        break;
      case "BinModeChange":
        switch (payload.event.binMode) {
          case "P":
            colorToReturn = mainColours.label["4"];
            lettersToReturn = "PLR";
            break;
          case "O":
            colorToReturn = mainColours.label["7"];
            lettersToReturn = "BO";
            break;
          case "C":
            colorToReturn = mainColours.label["8"];
            lettersToReturn = "BC";
            break;
        }
        break;
    }
  } else if (isHandToggled(payload)) {
    colorToReturn = payload.event.raiseHand
      ? andonColors.handRaisedYellow
      : "#90EE90"; // Light green for hand down
    lettersToReturn = payload.event.raiseHand ? "HU" : "HD";
  }

  // NEW HUB-based signalR events
  if (
    typeof hub === "string" &&
    typeof payload === "object" &&
    "eventType" in payload &&
    typeof payload.eventType === "string"
  ) {
    switch (hub) {
      case "pick":
        switch (payload.eventType) {
          case "PickCreated":
            colorToReturn = mainColours.label["1"];
            lettersToReturn = "PK";
            break;
          case "PickCompleted":
            colorToReturn = mainColours.label["10"];
            lettersToReturn = "PC";
            break;
        }
        break;
      case "tote":
        switch (payload.eventType) {
          case "On Workstation":
            colorToReturn = mainColours.label["5"];
            lettersToReturn = "TW";
            break;
          case "In Transit":
            colorToReturn = mainColours.label["3"];
            lettersToReturn = "TT";
            break;
        }
        break;
    }
  }

  return {
    color: colorToReturn,
    letters: lettersToReturn
  };
};

// good / bad / neutral status for an event history entry.
export const mapEventStatus = (
  event: AndonEventWithMeta
): "good" | "bad" | "neutral" => {
  // If the event has an explicit andon status, use it
  if (event.status) {
    return event.status;
  }

  // For certain event types, derive andon-style status
  const { event: payload } = event;

  if (isAutostoreEvent(payload)) {
    switch (payload.case) {
      case "PortError":
        return payload.event.hasError ? "bad" : "good";
      case "SystemModeChanged":
        return ["ALT", "DSTP", "STP", "SVC"].includes(payload.event.systemMode)
          ? "bad"
          : "good";
      case "HandToggled":
        // Hand raised = needs attention (bad), hand down = resolved (good)
        return payload.event.raiseHand ? "bad" : "good";
      case "PortMode":
        // Port mode changes are generally neutral
        return "neutral";
      default:
        return "neutral";
    }
  } else if (isHandToggled(payload)) {
    // Hand raised = needs attention (bad), hand down = resolved (good)
    return payload.event.raiseHand ? "bad" : "good";
  }

  // For other events, default to neutral
  return "neutral";
};

// Hex color for event status badges.
export const getEventStatusColor = (
  status: "good" | "bad" | "neutral"
): string => {
  switch (status) {
    case "bad":
      return "#E11B22"; // Red for bad status
    case "good":
      return "#15844B"; // Green for good status
    case "neutral":
      return "#757575"; // Gray for neutral
    default:
      return "#757575"; // Default gray
  }
};

// Icon component for a workstation mode, if any.
export function getWorkstationModeIcon(
  mode: WorkstationSummaryDto["mode"]
): React.ElementType | null {
  switch (mode) {
    case "Induction":
      return ModeGoodsIn20Px;
    case "Picking":
      return ModeGoodsOut20Px;
    case "Maintenance":
      return Service20Px;
    case "Problem Solving":
    case "Cycle Count":
      return Diagnose20Px;
    case "Standby":
      return Clock20Px;
    default:
      return null;
  }
}

// Tile font size clamped from cell height.
export function computeFontSize({
  heightPx,
  upperFontSizeLimit = 25,
  lowerFontSizeLimit = 9,
  fontSizeMultiplier = 0.2
}: {
  heightPx: number;
  upperFontSizeLimit: number;
  lowerFontSizeLimit: number;
  fontSizeMultiplier: number;
}): string {
  const unclampedFontHeight = heightPx * fontSizeMultiplier;

  const clamped = Math.max(
    Math.min(unclampedFontHeight, upperFontSizeLimit),
    lowerFontSizeLimit
  );

  return `${clamped}px`;
}

// Stable string key for a grid cell coordinate.
export const calculateAndonGridCellKey = (cell: {
  x: number;
  y: number;
}): string => `${cell.x},${cell.y}`;

// Human-readable 1-based cell coordinates for UI.
export const formatAndonGridCellCoordinates = (cell: {
  x: number;
  y: number;
}): string => `(${cell.x + 1}, ${cell.y + 1})`;

// Scoped port map key: gridId:portId.
export const calculateAndonPortKey = (
  gridId: string,
  portId: string | number
): string => `${gridId}:${portId}`;

/**
 * Look up an entry in maps like portStatusByPortKey / portHasBinsByPortKey.
 *
 * Keys are written as:
 * - `${gridId}:${portId}` — e.g. EventListening PortError / BinModeChange when event.gridId is set; workstation-fetch seed of bins
 * - `${portId}` — e.g. SignalR event without gridId, or setPortStatus / setPortHasBins called with only portId
 *
 * If gridId is passed, try the scoped key first; if absent, fall back to bare portId.
 * Callers (once wired): selectPortStatus, selectPortHasBins, selectWorkstationStatus, EventListening workstationIdForPort.
 *
 * Bare-key fallback is transitional/legacy once every andon update always includes gridId.
 */
export const getAndonPortEntry = <T,>(
  map: Record<string, T>,
  portId: string,
  gridId?: string | null
): T | undefined => {
  if (gridId) {
    const keyed = map[calculateAndonPortKey(gridId, portId)];
    if (keyed !== undefined) {
      return keyed;
    }
  }
  return map[portId];
};

// All grid cells in the axis-aligned rectangle between two corners.
export const cellsInRect = (
  a: { x: number; y: number },
  b: { x: number; y: number }
): Array<{ x: number; y: number }> => {
  const minX = Math.min(a.x, b.x);
  const maxX = Math.max(a.x, b.x);
  const minY = Math.min(a.y, b.y);
  const maxY = Math.max(a.y, b.y);
  const cells: Array<{ x: number; y: number }> = [];
  for (let y = minY; y <= maxY; y += 1) {
    for (let x = minX; x <= maxX; x += 1) {
      cells.push({ x, y });
    }
  }
  return cells;
};

// Border records on the outer edge of a cell selection.
export const buildPerimeterCellBorders = (
  cells: Array<{ x: number; y: number }>,
  sides: BorderSide[],
  color: string
): AndonCellBorder[] => {
  if (cells.length === 0 || sides.length === 0) {
    return [];
  }

  const minX = Math.min(...cells.map((cell) => cell.x));
  const maxX = Math.max(...cells.map((cell) => cell.x));
  const minY = Math.min(...cells.map((cell) => cell.y));
  const maxY = Math.max(...cells.map((cell) => cell.y));

  const borders: AndonCellBorder[] = [];
  for (const cell of cells) {
    const cellSides: BorderSide[] = [];
    if (sides.includes("top") && cell.y === minY) {
      cellSides.push("top");
    }
    if (sides.includes("bottom") && cell.y === maxY) {
      cellSides.push("bottom");
    }
    if (sides.includes("left") && cell.x === minX) {
      cellSides.push("left");
    }
    if (sides.includes("right") && cell.x === maxX) {
      cellSides.push("right");
    }
    if (cellSides.length > 0) {
      borders.push({
        x: cell.x,
        y: cell.y,
        sides: cellSides,
        color
      });
    }
  }
  return borders;
};

// Map a screen pointer position to a grid cell, or null if outside the grid.
export const clientPointToAndonGridCell = ({
  clientX,
  clientY,
  gridElement,
  columns,
  rows,
  rowPitch,
  fallbackWidth
}: {
  clientX: number;
  clientY: number;
  gridElement: Element;
  columns: number;
  rows: number;
  rowPitch: number;
  fallbackWidth?: number;
}): { x: number; y: number } | null => {
  if (columns <= 0 || rows <= 0 || rowPitch <= 0) {
    return null;
  }
  const rect = gridElement.getBoundingClientRect();
  const margin = BORDER_OVERLAY_RGL_MARGIN;
  const width = rect.width > 0 ? rect.width : (fallbackWidth ?? 0);
  if (width <= 0) {
    return null;
  }
  const gridHeight = rect.height > 0 ? rect.height : rows * rowPitch + margin;
  if (
    clientX < rect.left ||
    clientY < rect.top ||
    clientX > rect.left + width ||
    clientY > rect.top + gridHeight
  ) {
    return null;
  }
  const columnPitch = (width - margin) / columns;
  if (columnPitch <= margin) {
    return null;
  }
  const cellWidth = columnPitch - margin;
  const cellHeight = rowPitch - margin;
  const relativeX = clientX - rect.left - margin / 2;
  const relativeY = clientY - rect.top - margin / 2;
  if (relativeX < 0 || relativeY < 0) {
    return null;
  }
  const x = Math.floor(relativeX / columnPitch);
  const y = Math.floor(relativeY / rowPitch);
  if (x < 0 || y < 0 || x >= columns || y >= rows) {
    return null;
  }
  const offsetX = relativeX - x * columnPitch;
  const offsetY = relativeY - y * rowPitch;
  if (offsetX >= cellWidth || offsetY >= cellHeight) {
    return null;
  }
  return { x, y };
};

// Event explorer color for a SignalR hub type.
export const getHubColor = (hub: string): string => {
  switch (hub) {
    case "grid":
      return "#2563eb"; // blue
    case "pick":
      return "#dc2626"; // red
    case "ptl":
      return "#ea580c"; // orange
    case "tote":
      return "#16a34a"; // green
    default:
      return "#6b7280"; // gray
  }
};

// Type guard for text label layout tiles.
export const isTextLayoutItem = (
  boardLayoutItem: AndonBoardLayoutItem
): boardLayoutItem is AndonBoardTextLayoutItem =>
  boardLayoutItem.kind === "text";

// Type guard for workstation layout tiles (includes legacy items without kind).
export const isWorkstationLayoutItem = (
  boardLayoutItem: AndonBoardLayoutItem
): boardLayoutItem is AndonBoardWorkstationLayoutItem =>
  boardLayoutItem.kind === "workstation" || boardLayoutItem.kind === undefined;

// FC ids from saved layout metadata.
export const getLayoutFcIds = (layout: BoardLayout): string[] => {
  if (layout.fulfillmentCenterIds?.length) return layout.fulfillmentCenterIds;
  if (layout.fulfillmentCenterId) return [layout.fulfillmentCenterId];
  return [];
};

// FC ids referenced by workstation tiles on the board.
export const getBoardFetchFcIds = (
  layout: AndonBoardLayoutItem[]
): string[] => [
  ...new Set(
    layout
      .filter(isWorkstationLayoutItem)
      .map((boardLayoutItem) => boardLayoutItem.fulfillmentCenterId)
      .filter((fulfillmentCenterId): fulfillmentCenterId is string =>
        Boolean(fulfillmentCenterId)
      )
  )
];

// Union of layout-level and tile-level FC ids to fetch workstations for.
export const resolveBoardFetchFcIds = ({
  layoutLevelFcIds,
  layoutItems
}: {
  layoutLevelFcIds: string[];
  layoutItems: AndonBoardLayoutItem[];
}): string[] => [
  ...new Set([...layoutLevelFcIds, ...getBoardFetchFcIds(layoutItems)])
];

// Convert legacy textElements entries into text layout tiles.
export const migrateLegacyTextLayoutItems = (
  layout: AndonBoardLayoutItem[],
  textElements?: BoardLayout["textElements"]
): AndonBoardLayoutItem[] => {
  const textById = new Map(
    (textElements ?? []).map((textElement) => [textElement.id, textElement])
  );

  return layout.flatMap((boardLayoutItem): AndonBoardLayoutItem[] => {
    if (isTextLayoutItem(boardLayoutItem)) {
      return [boardLayoutItem];
    }

    if (!boardLayoutItem.i.startsWith(TEXT_ELEMENT_PREFIX)) {
      return [boardLayoutItem];
    }

    const textElementId = boardLayoutItem.i.slice(TEXT_ELEMENT_PREFIX.length);
    const textElement = textById.get(textElementId);
    if (!textElement) {
      return [];
    }

    const textLayoutItem: AndonBoardTextLayoutItem = {
      kind: "text",
      i: boardLayoutItem.i,
      x: boardLayoutItem.x,
      y: boardLayoutItem.y,
      w: boardLayoutItem.w,
      h: boardLayoutItem.h,
      maxH: boardLayoutItem.maxH,
      text: textElement.text,
      backgroundColor: textElement.backgroundColor,
      textColor: textElement.textColor,
      fontSize: textElement.fontSize
    };
    return [textLayoutItem];
  });
};

// Whether workstation data is loaded for the layout's fulfillment centers.
export const isBoardWorkstationsReady = ({
  layoutFcIds,
  siteFcWorkstations,
  workstations
}: {
  layoutFcIds: string[];
  siteFcWorkstations: WorkstationWithFulfillmentCenter[] | undefined;
  workstations: WorkstationWithFulfillmentCenter[] | undefined;
}): boolean =>
  layoutFcIds.length === 0
    ? siteFcWorkstations !== undefined
    : workstations !== undefined;

// Stamp site FC on tiles missing fulfillmentCenterId (legacy layouts).
export const applySiteFcToLegacyLayoutItems = (
  layout: AndonBoardLayoutItem[],
  siteFulfillmentCenterId?: string | null,
  layoutLevelFcIds: string[] = []
): AndonBoardLayoutItem[] => {
  if (!siteFulfillmentCenterId || layoutLevelFcIds.length > 0) return layout;

  return layout.map((boardLayoutItem) =>
    isTextLayoutItem(boardLayoutItem) || boardLayoutItem.fulfillmentCenterId
      ? boardLayoutItem
      : { ...boardLayoutItem, fulfillmentCenterId: siteFulfillmentCenterId }
  );
};

// Saved BoardLayout → editable WorkingLayout for the layout editor.
export const toWorkingLayout = (
  saved: BoardLayout,
  siteFulfillmentCenterId?: string | null
): WorkingLayout => {
  const migratedLayout = migrateLegacyTextLayoutItems(
    saved.layout,
    saved.textElements
  );
  const layoutLevelFcIds = getLayoutFcIds(saved);
  return {
    name: saved.name,
    dimensions: { ...saved.dimensions },
    layout: applySiteFcToLegacyLayoutItems(
      migratedLayout,
      siteFulfillmentCenterId,
      layoutLevelFcIds
    ),
    scale: saved.scale,
    displayLines: saved.displayLines ?? DEFAULT_ANDON_DISPLAY_LINES,
    cellBorders: [...(saved.cellBorders ?? [])],
    fulfillmentCenterIds: layoutLevelFcIds
  };
};

// WorkingLayout → persistable BoardLayout for save.
export const fromWorkingLayout = (
  working: WorkingLayout,
  layoutName: string
): BoardLayout => {
  const fulfillmentCenterIds = resolveBoardFetchFcIds({
    layoutLevelFcIds: working.fulfillmentCenterIds,
    layoutItems: working.layout
  });
  return {
    name: layoutName,
    dimensions: working.dimensions,
    layout: working.layout,
    scale: working.scale,
    displayLines: working.displayLines,
    fulfillmentCenterIds:
      fulfillmentCenterIds.length > 0 ? fulfillmentCenterIds : undefined,
    cellBorders:
      working.cellBorders.length > 0 ? working.cellBorders : undefined
  };
};

// Pair workstation ids with FC ids for board placement.
export const resolveWorkstationSelections = ({
  workstationIds,
  fetchedWorkstations,
  fcWorkstationsById,
  selectedFcIds,
  siteFulfillmentCenterId
}: {
  workstationIds: string[];
  fetchedWorkstations: AndonWorkstation[];
  fcWorkstationsById: Record<string, AndonWorkstation[]>;
  selectedFcIds: string[];
  siteFulfillmentCenterId?: string | null;
}): AndonBoardWorkstationSelection[] =>
  workstationIds.flatMap((workstationId) => {
    const workstation = fetchedWorkstations.find(
      (candidateWorkstation) =>
        candidateWorkstation.workstationId === workstationId
    );
    const fulfillmentCenterId =
      workstation?.fulfillmentCenterId ??
      selectedFcIds.find((fulfillmentCenterId) =>
        fcWorkstationsById[fulfillmentCenterId]?.some(
          (candidateWorkstation) =>
            candidateWorkstation.workstationId === workstationId
        )
      ) ??
      (selectedFcIds.length === 0 ? siteFulfillmentCenterId : undefined);

    return fulfillmentCenterId ? [{ workstationId, fulfillmentCenterId }] : [];
  });

const boardTilesEqual = (
  leftLayout: AndonBoardLayoutItem[],
  rightLayout: AndonBoardLayoutItem[]
): boolean =>
  leftLayout.length === rightLayout.length &&
  leftLayout.every((leftItem, index) => {
    const rightItem = rightLayout[index];
    if (
      leftItem.i !== rightItem.i ||
      leftItem.x !== rightItem.x ||
      leftItem.y !== rightItem.y ||
      leftItem.w !== rightItem.w ||
      leftItem.h !== rightItem.h
    ) {
      return false;
    }

    if (isTextLayoutItem(leftItem) || isTextLayoutItem(rightItem)) {
      return (
        isTextLayoutItem(leftItem) &&
        isTextLayoutItem(rightItem) &&
        leftItem.text === rightItem.text &&
        leftItem.backgroundColor === rightItem.backgroundColor &&
        leftItem.textColor === rightItem.textColor &&
        leftItem.fontSize === rightItem.fontSize
      );
    }

    return leftItem.fulfillmentCenterId === rightItem.fulfillmentCenterId;
  });

const cellBordersEqual = (
  leftBorders: AndonCellBorder[],
  rightBorders: AndonCellBorder[]
): boolean => {
  const signature = (borders: AndonCellBorder[]) =>
    JSON.stringify(
      borders
        .map(({ x, y, sides, color }) => ({
          x,
          y,
          sides: [...(sides ?? [])].sort(),
          color
        }))
        .sort((left, right) => left.y - right.y || left.x - right.x)
    );

  return signature(leftBorders) === signature(rightBorders);
};

/** Geometry-only equality for react-grid-layout Layout arrays. */
export const boardLayoutsEqual = (
  leftLayout: Layout[],
  rightLayout: Layout[]
): boolean =>
  leftLayout.length === rightLayout.length &&
  leftLayout.every(
    (leftItem, index) =>
      leftItem.i === rightLayout[index].i &&
      leftItem.x === rightLayout[index].x &&
      leftItem.y === rightLayout[index].y &&
      leftItem.w === rightLayout[index].w &&
      leftItem.h === rightLayout[index].h
  );

// Apply react-grid-layout drag results onto board layout items.
export const mergeGridLayoutChange = (
  previousLayout: AndonBoardLayoutItem[],
  updatedLayout: Layout[],
  rows: number
): AndonBoardLayoutItem[] =>
  updatedLayout.map((gridLayoutItem) => {
    const existingLayoutItem = previousLayout.find(
      (previousLayoutItem) => previousLayoutItem.i === gridLayoutItem.i
    );
    return {
      ...existingLayoutItem,
      ...gridLayoutItem,
      maxH: rows - gridLayoutItem.y
    } as AndonBoardLayoutItem;
  });

// Full working layout equality (tiles, borders, dimensions, display lines, FCs).
export const layoutsAreEqual = (
  leftLayout: WorkingLayout,
  rightLayout: WorkingLayout
): boolean => {
  const displayLinesMatch =
    leftLayout.displayLines.length === rightLayout.displayLines.length &&
    leftLayout.displayLines.every(
      (line, index) => line === rightLayout.displayLines[index]
    );

  const fulfillmentCenterIdsMatch =
    leftLayout.fulfillmentCenterIds.length ===
      rightLayout.fulfillmentCenterIds.length &&
    leftLayout.fulfillmentCenterIds.every(
      (fulfillmentCenterId, index) =>
        fulfillmentCenterId === rightLayout.fulfillmentCenterIds[index]
    );

  return (
    leftLayout.name === rightLayout.name &&
    leftLayout.dimensions.rows === rightLayout.dimensions.rows &&
    leftLayout.dimensions.columns === rightLayout.dimensions.columns &&
    leftLayout.scale === rightLayout.scale &&
    displayLinesMatch &&
    fulfillmentCenterIdsMatch &&
    boardTilesEqual(leftLayout.layout, rightLayout.layout) &&
    cellBordersEqual(leftLayout.cellBorders, rightLayout.cellBorders)
  );
};

// Whether editor state diverges from the saved layout.
export const persistedLayoutDiffersFromSaved = (
  saved: BoardLayout,
  current: {
    layout: AndonBoardLayoutItem[];
    dimensions: { rows: number; columns: number };
    scale: number;
    displayLines: AndonDisplayLine[];
    cellBorders: AndonCellBorder[];
  }
): boolean =>
  !layoutsAreEqual(
    {
      name: saved.name,
      dimensions: current.dimensions,
      layout: current.layout,
      scale: current.scale,
      displayLines: current.displayLines,
      cellBorders: current.cellBorders,
      fulfillmentCenterIds: getLayoutFcIds(saved)
    },
    {
      name: saved.name,
      dimensions: saved.dimensions,
      layout: saved.layout,
      scale: saved.scale,
      displayLines: saved.displayLines ?? DEFAULT_ANDON_DISPLAY_LINES,
      cellBorders: saved.cellBorders ?? [],
      fulfillmentCenterIds: getLayoutFcIds(saved)
    }
  );

// FC abbreviation per workstation id for tile badges.
export const buildWorkstationFcAbbreviationById = (
  workstations: WorkstationWithFulfillmentCenter[] | undefined,
  fulfillmentCenters: FulfillmentCenterDto[],
  siteFulfillmentCenterId?: string | null
): Record<string, string> => {
  const abbreviationByFcId = Object.fromEntries(
    fulfillmentCenters.map((fc) => [
      fc.fulfillmentCenterId,
      fc.abbreviation || fc.name
    ])
  );

  return Object.fromEntries(
    (workstations ?? [])
      .map((workstation) => {
        const fcId =
          workstation.fulfillmentCenterId ??
          siteFulfillmentCenterId ??
          undefined;
        const abbreviation = fcId ? abbreviationByFcId[fcId] : undefined;
        return abbreviation ? ([workstation.id, abbreviation] as const) : null;
      })
      .filter((entry): entry is readonly [string, string] => entry !== null)
  );
};

// Grow row and column count to fit more tiles.
export const expandDimensionsToFit = (
  dimensions: BoardDimensions,
  totalItems: number
): BoardDimensions => {
  const capacity = dimensions.columns * dimensions.rows;
  if (totalItems <= capacity) {
    return dimensions;
  }

  const columns =
    dimensions.columns > 0
      ? dimensions.columns
      : Math.max(1, Math.ceil(Math.sqrt(totalItems)));
  const rows = Math.max(1, Math.ceil(totalItems / columns));
  return { columns, rows };
};

// Minimum rows and columns that contain every tile on the board.
export function calculateMinAndonBoardDimensions(
  layout: AndonBoardLayoutItem[]
): BoardDimensions {
  if (layout.length === 0) {
    return { columns: 1, rows: 1 };
  }

  let columns = 1;
  let rows = 1;
  for (const boardLayoutItem of layout) {
    columns = Math.max(columns, boardLayoutItem.x + boardLayoutItem.w);
    rows = Math.max(rows, boardLayoutItem.y + boardLayoutItem.h);
  }
  return { columns, rows };
}

// Place new workstation selections into empty grid cells.
export const addWorkstationsToBoard = (
  existingLayout: AndonBoardLayoutItem[],
  dimensions: BoardDimensions,
  workstationsToAdd: AndonBoardWorkstationSelection[]
) => {
  const occupiedIds = existingLayout.map(
    (boardLayoutItem) => boardLayoutItem.i
  );
  const newSelections = workstationsToAdd.filter(
    (selection) => !occupiedIds.includes(selection.workstationId)
  );

  if (newSelections.length === 0) {
    return { layout: existingLayout, dimensions };
  }

  const totalItems = existingLayout.length + newSelections.length;
  const expandedDimensions = expandDimensionsToFit(dimensions, totalItems);
  const emptyPositions = getEmptyPositions(
    existingLayout,
    expandedDimensions.columns,
    expandedDimensions.rows
  );

  const additions = newSelections.map((selection, index) => {
    const position = emptyPositions[index];
    return {
      kind: "workstation" as const,
      i: selection.workstationId,
      fulfillmentCenterId: selection.fulfillmentCenterId,
      x: position.x,
      y: position.y,
      w: 1,
      h: 1,
      maxH: expandedDimensions.rows - position.y
    };
  });

  return {
    layout: [...existingLayout, ...additions],
    dimensions: expandedDimensions
  };
};

// Translated label for a workstation tile display line key.
export const getAndonDisplayLineLabel = (
  line: AndonDisplayLine,
  t: TFunction
): string => t(ANDON_DISPLAY_LINE_LABEL_KEY[line]);

// Comma-separated roles label for workstation tiles.
export const formatAndonWorkstationRoles = (
  roles: string[] | undefined
): string | undefined => {
  if (!roles?.length) return undefined;
  const label = roles
    .map((role) => role.trim())
    .filter(Boolean)
    .join(", ");
  return label || undefined;
};

// Tooltip text for display lines not shown on the tile.
export const buildHiddenAndonTooltip = (
  displayLines: AndonDisplayLine[] | undefined,
  content: AndonWorkstationTooltipContent,
  t: TFunction
): string => {
  const shown = displayLines ?? DEFAULT_ANDON_DISPLAY_LINES;
  const hiddenFields: Array<{ line: AndonDisplayLine; value?: string }> = [
    { line: "icon", value: content.iconLabel },
    { line: "status", value: content.statusText },
    { line: "deviceId", value: content.deviceId },
    { line: "userName", value: content.userName },
    { line: "fcAbbreviation", value: content.fcAbbreviation },
    { line: "roles", value: content.roles }
  ];

  return hiddenFields
    .filter(({ line, value }) => value && !shown.includes(line))
    .map(({ line, value }) => `${getAndonDisplayLineLabel(line, t)}: ${value}`)
    .join("\n");
};
